import { Checkbox, Popover, Select, SelectItem, Stack } from '@mantine/core';
import { GridApi, IFilterParams, IFloatingFilterParams } from 'ag-grid-community';
import { DeviceTypeSelect } from 'components/selects/DeviceTypeSelect';
import { OrganizationSelect } from 'components/selects/OrganizationSelect';
import { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { get, isEqual, noop } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

type EvidenceFieldScopeMode = 'specific' | 'globalOnly';

export type EvidenceFieldScopeFilterModel = (
  | { mode: 'globalOnly' }
  | { mode: 'specific'; entityId: string; includeGlobal: boolean }
) & { filter?: never };

interface EvidenceFieldScopeFilterState {
  mode: EvidenceFieldScopeMode | null;
  entityId: string | null;
  includeGlobal: boolean;
}

interface EvidenceFieldScopeFilterConfig {
  filterKey: 'structure' | 'device';
  globalField: 'allOrganizations' | 'allDeviceTypes';
  associationsField: 'organizations' | 'deviceTypes';
  associationIdField: 'organizationId' | 'deviceTypeId';
  associationLabelField: 'organizationName' | 'deviceTypeName';
  specificModeLabel: string;
  entityPlaceholder: string;
  filterAriaLabel: string;
  modeAriaLabel: string;
  entityAriaLabel: string;
  entityKind: 'organization' | 'deviceType';
}

interface EvidenceFieldScopeFloatingFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
  parentFilterInstance?: IFloatingFilterParams['parentFilterInstance'];
}

interface EvidenceFieldScopeFloatingFilterHandle {
  onParentModelChanged: () => void;
}

interface EvidenceFieldScopeEntityOption {
  label: string;
  value: string | number;
}

const DEFAULT_STATE: EvidenceFieldScopeFilterState = {
  mode: null,
  entityId: null,
  includeGlobal: true,
};

const AVAILABILITY_CONFIG: EvidenceFieldScopeFilterConfig = {
  filterKey: 'structure',
  globalField: 'allOrganizations',
  associationsField: 'organizations',
  associationIdField: 'organizationId',
  associationLabelField: 'organizationName',
  specificModeLabel: 'V organizácii',
  entityPlaceholder: 'Vyberte organizáciu',
  filterAriaLabel: 'Filter dostupnosti',
  modeAriaLabel: 'Režim filtra dostupnosti',
  entityAriaLabel: 'Organizácia filtra dostupnosti',
  entityKind: 'organization',
};

const DEVICE_RESTRICTION_CONFIG: EvidenceFieldScopeFilterConfig = {
  filterKey: 'device',
  globalField: 'allDeviceTypes',
  associationsField: 'deviceTypes',
  associationIdField: 'deviceTypeId',
  associationLabelField: 'deviceTypeName',
  specificModeLabel: 'Pre typ zariadenia',
  entityPlaceholder: 'Vyberte typ zariadenia',
  filterAriaLabel: 'Filter vymedzenia zariadení',
  modeAriaLabel: 'Režim filtra vymedzenia zariadení',
  entityAriaLabel: 'Typ zariadenia filtra vymedzenia',
  entityKind: 'deviceType',
};

/**
 * Converts a persisted filter model into local UI state.
 */
function getStateFromModel(model?: EvidenceFieldScopeFilterModel | null): EvidenceFieldScopeFilterState {
  if (model?.mode === 'globalOnly') {
    return { mode: 'globalOnly', entityId: null, includeGlobal: true };
  }

  if (model?.mode === 'specific' && model.entityId) {
    return { mode: 'specific', entityId: String(model.entityId), includeGlobal: model.includeGlobal !== false };
  }

  return DEFAULT_STATE;
}

/**
 * Converts local UI state into the persisted AG Grid filter model.
 */
function createModel(state: EvidenceFieldScopeFilterState): EvidenceFieldScopeFilterModel | null {
  if (state.mode === 'globalOnly') {
    return { mode: 'globalOnly' };
  }

  if (state.mode === 'specific' && state.entityId) {
    return { mode: 'specific', entityId: state.entityId, includeGlobal: state.includeGlobal };
  }

  return null;
}

/**
 * Creates the compact value displayed in the floating-filter row.
 */
function getSummary(
  state: EvidenceFieldScopeFilterState,
  specificModeLabel: string,
  entityLabels: Record<string, string>
): string {
  const model = createModel(state);

  if (model?.mode === 'globalOnly') {
    return 'Iba globálne';
  }

  if (model?.mode === 'specific') {
    const entityLabel = entityLabels[model.entityId] ?? specificModeLabel;
    return `${entityLabel}${model.includeGlobal ? ' + globálne' : ''}`;
  }

  return '';
}

/**
 * Resolves a restored entity label from evidence-field row associations.
 */
function getEntityLabelFromGrid(api: GridApi, config: EvidenceFieldScopeFilterConfig, entityId: string): string | null {
  let entityLabel: string | null = null;

  api.forEachNode(({ data }) => {
    if (entityLabel || !data) return;

    const associations = get(data, config.associationsField);
    if (!Array.isArray(associations)) return;

    const association = associations.find(
      (candidate) => String(get(candidate, config.associationIdField)) === entityId
    );
    const label = get(association, config.associationLabelField);
    if (label) entityLabel = String(label);
  });

  return entityLabel;
}

/**
 * Shared AG Grid parent filter for organization and device-type evidence-field scopes.
 */
const EvidenceFieldScopeColumnFilter = forwardRef(
  (
    { filterChangedCallback, config }: IFilterParams & { config: EvidenceFieldScopeFilterConfig },
    ref: React.ForwardedRef<unknown>
  ) => {
    const model = useRef<EvidenceFieldScopeFilterModel | null>(null);

    useImperativeHandle(ref, () => ({
      doesFilterPass({ data }: { data: Record<string, unknown> }) {
        const currentModel = model.current;

        if (!currentModel) {
          return true;
        }

        const isGlobal = Boolean(get(data, config.globalField));

        if (currentModel.mode === 'globalOnly') {
          return isGlobal;
        }

        if (currentModel.includeGlobal && isGlobal) {
          return true;
        }

        const associations = get(data, config.associationsField);

        return (
          Array.isArray(associations) &&
          associations.some(
            (association) => String(get(association, config.associationIdField)) === currentModel.entityId
          )
        );
      },

      getModel() {
        return model.current;
      },

      setModel(newModel: EvidenceFieldScopeFilterModel | null) {
        model.current = createModel(getStateFromModel(newModel));
      },

      isFilterActive() {
        return model.current !== null;
      },

      onFloatingFilterChanged(newModel: EvidenceFieldScopeFilterModel | null) {
        const normalizedModel = createModel(getStateFromModel(newModel));

        if (!isEqual(model.current, normalizedModel)) {
          model.current = normalizedModel;
          filterChangedCallback();
        }
      },
    }));

    return <></>;
  }
);

/**
 * Creates a configured parent filter for one evidence-field scope column.
 */
function createColumnFilter(config: EvidenceFieldScopeFilterConfig) {
  return forwardRef((props: IFilterParams, ref: React.ForwardedRef<unknown>) => (
    <EvidenceFieldScopeColumnFilter {...props} config={config} ref={ref} />
  ));
}

/**
 * Shared floating-filter UI for organization and device-type scope filtering.
 */
const EvidenceFieldScopeFloatingFilter = forwardRef<
  EvidenceFieldScopeFloatingFilterHandle,
  EvidenceFieldScopeFloatingFilterProps & { config: EvidenceFieldScopeFilterConfig }
>(function EvidenceFieldScopeFloatingFilter({ api, config, onChange, parentFilterInstance = noop }, ref) {
  const [state, setState] = useState<EvidenceFieldScopeFilterState>(() =>
    getStateFromModel(api.getFilterModel()[config.filterKey])
  );
  const [entityLabels, setEntityLabels] = useState<Record<string, string>>({});
  const [opened, setOpened] = useState(false);

  useImperativeHandle(ref, () => ({ onParentModelChanged: noop }));

  const pushModel = useCallback(
    (nextState: EvidenceFieldScopeFilterState) => {
      const nextModel = createModel(nextState);

      if (onChange) {
        onChange({ [config.filterKey]: nextModel });
        return;
      }

      parentFilterInstance((instance: unknown) => {
        (
          instance as {
            onFloatingFilterChanged: (model: EvidenceFieldScopeFilterModel | null) => void;
          }
        ).onFloatingFilterChanged(nextModel);
      });
    },
    [config.filterKey, onChange, parentFilterInstance]
  );

  const setAndPush = useCallback(
    (nextState: EvidenceFieldScopeFilterState) => {
      setState(nextState);
      pushModel(nextState);
    },
    [pushModel]
  );

  useEffect(() => {
    /** Keeps the floating filter synchronized with applied and restored table profiles. */
    const handleFilterChanged = ({ api: gridApi }: { api: GridApi }) => {
      const model = gridApi.getFilterModel()[config.filterKey] as EvidenceFieldScopeFilterModel | null | undefined;

      setState((previous) =>
        !model && opened && previous.mode === 'specific' && !previous.entityId ? previous : getStateFromModel(model)
      );
    };

    api.addEventListener('filterChanged', handleFilterChanged);
    return () => api.removeEventListener('filterChanged', handleFilterChanged);
  }, [api, config.filterKey, opened]);

  const handleEntitiesReady = useCallback((options: EvidenceFieldScopeEntityOption[]) => {
    setEntityLabels(Object.fromEntries(options.map(({ value, label }) => [String(value), String(label)])));
  }, []);

  useEffect(() => {
    /** Resolves names when a saved filter is applied before the lazy entity select has loaded. */
    const handleModelUpdated = () => {
      if (state.mode !== 'specific' || !state.entityId) return;

      const entityLabel = getEntityLabelFromGrid(api, config, state.entityId);
      if (!entityLabel) return;

      setEntityLabels((labels) =>
        labels[state.entityId!] === entityLabel ? labels : { ...labels, [state.entityId!]: entityLabel }
      );
    };

    handleModelUpdated();
    api.addEventListener('modelUpdated', handleModelUpdated);
    return () => api.removeEventListener('modelUpdated', handleModelUpdated);
  }, [api, config, state.entityId, state.mode]);

  const summary = useMemo(
    () => getSummary(state, config.specificModeLabel, entityLabels),
    [config.specificModeLabel, entityLabels, state]
  );
  const modeOptions = useMemo<SelectItem[]>(
    () => [
      { value: 'specific', label: config.specificModeLabel },
      { value: 'globalOnly', label: 'Iba globálne' },
    ],
    [config.specificModeLabel]
  );

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" width={300} withinPortal>
      <Popover.Target>
        <Select
          aria-label={config.filterAriaLabel}
          data={[summary]}
          size="sm"
          w="100%"
          value={summary}
          placeholder="Všetky"
          onFocus={() => setOpened(true)}
          clearable={!!summary}
          onChange={(value) => {
            if (!value) {
              setAndPush(DEFAULT_STATE);
              setOpened(false);
            }
          }}
          styles={{ dropdown: { display: 'none' } }}
        />
      </Popover.Target>

      <Popover.Dropdown>
        <Stack spacing="sm">
          <Select
            aria-label={config.modeAriaLabel}
            data={modeOptions}
            value={state.mode}
            placeholder="Vyberte režim"
            clearable
            size="sm"
            onChange={(mode) =>
              setAndPush({
                mode: mode as EvidenceFieldScopeMode | null,
                entityId: null,
                includeGlobal: true,
              })
            }
          />

          {state.mode === 'specific' && (
            <>
              {config.entityKind === 'organization' ? (
                <OrganizationSelect
                  aria-label={config.entityAriaLabel}
                  lazyLoad
                  clearable
                  showDiscarded
                  size="sm"
                  w="100%"
                  placeholder={config.entityPlaceholder}
                  value={state.entityId}
                  onReady={handleEntitiesReady}
                  onChange={(entityId) => setAndPush({ ...state, entityId })}
                />
              ) : (
                <DeviceTypeSelect
                  aria-label={config.entityAriaLabel}
                  lazyLoad
                  clearable
                  listSubtypes
                  size="sm"
                  w="100%"
                  placeholder={config.entityPlaceholder}
                  value={state.entityId}
                  onReady={handleEntitiesReady}
                  onChange={(entityId) => setAndPush({ ...state, entityId })}
                />
              )}

              <Checkbox
                label="Zahrnúť globálne"
                checked={state.includeGlobal}
                onChange={(event) => setAndPush({ ...state, includeGlobal: event.currentTarget.checked })}
              />
            </>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
});

/**
 * Creates a configured floating filter for one evidence-field scope column.
 */
function createFloatingFilter(config: EvidenceFieldScopeFilterConfig) {
  return forwardRef<EvidenceFieldScopeFloatingFilterHandle, EvidenceFieldScopeFloatingFilterProps>((props, ref) => (
    <EvidenceFieldScopeFloatingFilter {...props} config={config} ref={ref} />
  ));
}

export const EvidenceFieldAvailabilityColumnFilter = createColumnFilter(AVAILABILITY_CONFIG);
export const EvidenceFieldAvailabilityFloatingFilter = createFloatingFilter(AVAILABILITY_CONFIG);
export const EvidenceFieldDeviceRestrictionColumnFilter = createColumnFilter(DEVICE_RESTRICTION_CONFIG);
export const EvidenceFieldDeviceRestrictionFloatingFilter = createFloatingFilter(DEVICE_RESTRICTION_CONFIG);
